import * as React from "react"
import { AlertCircle } from "lucide-react"
import {
  ConfigPropertiesPanel,
  type ConfigProperty,
  type ConfigPropertyValue,
  type EnumOption,
} from "@/components/alfred/config-properties-panel"
import { Card, CardContent } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import {
  OPERATING_MODE_OPTIONS,
  type AppleVendor,
  type DisplayConfigurationItem,
  type GoogleVendor,
  type ProVenueInstance,
  type ProVenueInstanceInput,
} from "@/types/alfred-config"
import {
  ADMIN_IDLE_OPTIONS,
  DISPLAY_TIME_OPTIONS,
  OFFLINE_BATCH_OPTIONS,
  SOUND_INVALID_OPTIONS, SOUND_VALID_OPTIONS,
  SOUND_WARNING_OPTIONS, TURNSTILE_MODE_OPTIONS, UNLOCKED_ACCESS_OPTIONS,
  VALIDATION_VENDOR_OPTIONS,
} from "@/components/alfred/alfred-device-form-fields"
import type { AlfredDeviceConfig, AlfredDeviceConfigKey, UpdateAlfredDeviceRequest } from "@/types/alfred-device"
import { DeviceNotFoundState } from "@/components/shared/empty-states"
import { useAlfredDevice, useUpdateAlfredDeviceById } from "@/hooks/use-alfred-devices"
import { isNotFoundError } from "@/types/api"
import { AppleVendorEditor } from "@/components/alfred/apple-vendor-editor"
import { DisplayConfigurationEditor } from "@/components/alfred/display-configuration-editor"
import { GoogleVendorEditor } from "@/components/alfred/google-vendor-editor"
import { ProVenueInstanceEditor } from "@/components/alfred/pro-venue-instance-editor"
import {
  presentAlfredConfigFields,
} from "@/components/alfred/alfred-device-field-presentation"


type FieldDescriptor =
  | { key: AlfredDeviceConfigKey; type: "readonly" }
  | { key: AlfredDeviceConfigKey; type: "string"; placeholder?: string }
  | { key: AlfredDeviceConfigKey; type: "number"; min?: number; max?: number; step?: number }
  | { key: AlfredDeviceConfigKey; type: "boolean" }
  | { key: AlfredDeviceConfigKey; type: "string-array" }
  | {
      key: AlfredDeviceConfigKey
      type: "json"
      itemLabel?: string
      editor?: (
        value: unknown[],
        onChange: (next: unknown[]) => void,
        disabled: boolean,
        accessibleLabel: string,
        usesGlobalDefault: boolean,
      ) => React.ReactNode
    }
  | { key: AlfredDeviceConfigKey; type: "enum"; options: ReadonlyArray<EnumOption>; numeric?: boolean }
  | {
      key: "configurationPassword" | "ticketmasterConsumerKey"
      type: "secret"
      htmlPasswordInput?: boolean
      placeholder?: string
    }

const BASE_FIELD_DESCRIPTORS: FieldDescriptor[] = [
  { key: "deviceId", type: "readonly" },
  { key: "clientId", type: "readonly" },
  { key: "sotiDeviceId", type: "readonly" },
  { key: "venueCode", type: "readonly" },
  { key: "validationVendor", type: "enum", options: VALIDATION_VENDOR_OPTIONS },
  { key: "proVenueUser", type: "string" },
  {
    key: "proVenueInstances",
    type: "json",
    itemLabel: "instance",
    editor: (value, onChange, disabled, accessibleLabel, usesGlobalDefault) => (
      <ProVenueInstanceEditor
        items={value as ProVenueInstanceInput[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
        usesGlobalDefault={usesGlobalDefault}
      />
    ),
  },
  { key: "facilityCode", type: "string" },
  { key: "league", type: "string" },
  { key: "areaCode", type: "string" },
  { key: "deviceCode", type: "string" },
  { key: "offlineValidationMode", type: "boolean" },
  { key: "offlineBatchUploadLimit", type: "enum", options: OFFLINE_BATCH_OPTIONS, numeric: true },
  { key: "soundValidNumber", type: "enum", options: SOUND_VALID_OPTIONS, numeric: true },
  { key: "soundInvalidNumber", type: "enum", options: SOUND_INVALID_OPTIONS, numeric: true },
  { key: "soundWarningNumber", type: "enum", options: SOUND_WARNING_OPTIONS, numeric: true },
  { key: "maxCpuTemperature", type: "number" },
  { key: "minCpuTemperature", type: "number" },
  { key: "exitScanningAllowed", type: "boolean" },
  { key: "adminModeIdleWindow", type: "enum", options: ADMIN_IDLE_OPTIONS, numeric: true },
  { key: "validDisplayTime", type: "enum", options: DISPLAY_TIME_OPTIONS, numeric: true },
  { key: "invalidDisplayTime", type: "enum", options: DISPLAY_TIME_OPTIONS, numeric: true },
  { key: "operatingMode", type: "enum", options: OPERATING_MODE_OPTIONS },
  { key: "turnstileMode", type: "enum", options: TURNSTILE_MODE_OPTIONS, numeric: true },
  { key: "unlockedAccessTime", type: "enum", options: UNLOCKED_ACCESS_OPTIONS, numeric: true },
  { key: "isLockFailSafe", type: "boolean" },
  { key: "useSimpleResponse", type: "boolean" },
  { key: "mainBackgroundColor", type: "string" },
  { key: "secondaryBackgroundColor", type: "string" },
  { key: "idleStateColor", type: "string" },
  { key: "nfcEnabled", type: "boolean" },
  { key: "enableFan", type: "boolean" },
  { key: "enableHotspot", type: "boolean" },
  { key: "wakeLockEnabled", type: "boolean" },
  { key: "enableScanReset", type: "boolean" },
  { key: "enableManualValidationMask", type: "boolean" },
  {
    key: "displayConfiguration",
    type: "json",
    itemLabel: "screen",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <DisplayConfigurationEditor
        items={value as DisplayConfigurationItem[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  {
    key: "appleVendors",
    type: "json",
    itemLabel: "credential",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <AppleVendorEditor
        items={value as AppleVendor[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  {
    key: "googleVendors",
    type: "json",
    itemLabel: "credential",
    editor: (value, onChange, disabled, accessibleLabel) => (
      <GoogleVendorEditor
        items={value as GoogleVendor[]}
        onChange={(next) => onChange(next as unknown[])}
        disabled={disabled}
        triggerAriaLabel={accessibleLabel}
      />
    ),
  },
  { key: "ticketmasterVenueId", type: "string" },
  {
    key: "ticketmasterConsumerKey",
    type: "secret",
    placeholder: "Leave blank to keep current",
  },
  {
    key: "configurationPassword",
    type: "secret",
    htmlPasswordInput: true,
    placeholder: "Leave blank to keep current",
  },
]

const FIELD_DESCRIPTORS = presentAlfredConfigFields(BASE_FIELD_DESCRIPTORS, {
  requireCompleteSchema: true,
})

/** Fingerprint of the resolved device config payload (includes inherited global values). */
function deviceConfigResetKey(device: AlfredDeviceConfig): string {
  return JSON.stringify(device)
}

function configToProperties(config: AlfredDeviceConfig): ConfigProperty[] {
  const inherited = new Set(config.inheritedFromGlobal)
  return FIELD_DESCRIPTORS.map((field): ConfigProperty => {
    if (field.type === "secret") {
      return {
        label: field.label,
        section: field.section,
        key: field.key,
        type: "string",
        value: "",
        password: field.htmlPasswordInput,
        maskedCredential: !field.htmlPasswordInput,
        isInherited: inherited.has(field.key),
        placeholder: field.placeholder ?? "Leave blank to keep current",
      }
    }
    const raw = config[field.key]
    const isInherited = inherited.has(field.key)
    switch (field.type) {
      case "readonly":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "readonly",
          value: String(raw ?? ""),
        }
      case "string":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "string",
          value: (raw as string | undefined) ?? "",
          isInherited,
          placeholder: field.placeholder,
        }
      case "number":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "number",
          value: raw as number,
          isInherited,
          min: field.min,
          max: field.max,
          step: field.step,
        }
      case "boolean":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "boolean",
          value: raw as boolean,
          isInherited,
        }
      case "string-array":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "string-array",
          value: raw as unknown as string[],
          isInherited,
        }
      case "json": {
        // ProVenue instances arrive with only non-sensitive metadata. Seed the
        // editor's working value with blank write-only credentials so entered
        // secrets persist across sheet reopens but are never sourced from the API.
        const jsonValue =
          field.key === "proVenueInstances"
            ? ((raw as ProVenueInstance[] | undefined) ?? []).map((instance) => ({
                ...instance,
                consumerKey: "",
                consumerSecret: "",
              }))
            : (raw as unknown[])
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "json",
          value: jsonValue as unknown[],
          isInherited,
          itemLabel: field.itemLabel,
          fullWidth: field.key === "proVenueInstances",
          replaceInheritedAsCollection: field.key === "proVenueInstances",
          editor: field.editor,
        }
      }
      case "enum":
        return {
          label: field.label,
          section: field.section,
          key: field.key,
          type: "enum",
          value: raw as string | number,
          isInherited,
          options: field.options,
        }
    }
  })
}

function propertiesToUpdate(next: Record<string, ConfigPropertyValue | null>): UpdateAlfredDeviceRequest {
  const update: Record<string, ConfigPropertyValue | null> = {}
  for (const field of FIELD_DESCRIPTORS) {
    if (field.type === "readonly") continue
    const v = next[field.key]
    if (v === undefined) continue
    if (field.type === "secret") {
      if (v === null) {
        ;(update as Record<string, unknown>)[field.key] = { value: null, useDefault: true }
      } else if (typeof v === "string" && v !== "") {
        ;(update as Record<string, unknown>)[field.key] = { value: v, useDefault: false }
      }
      // empty string = no change, omit
      continue
    }
    if (v === null) { update[field.key] = null; continue }
    if (field.type === "number" && v === "") continue
    if (field.type === "enum" && field.numeric && typeof v === "string") {
      update[field.key] = Number(v)
      continue
    }
    update[field.key] = v
  }
  return update as UpdateAlfredDeviceRequest
}

interface AlfredDeviceConfigViewProps {
  id: number
  defaultEditing?: boolean
  onBackToDevices: () => void
  backToDevicesLabel?: string
}

export function AlfredDeviceConfigView({
  id,
  defaultEditing,
  onBackToDevices,
  backToDevicesLabel = "Back to Devices",
}: AlfredDeviceConfigViewProps) {
  const { toast } = useToast()
  const { data: device, error, isLoading, mutate } = useAlfredDevice(id)
  const update = useUpdateAlfredDeviceById(id)

  if (isLoading) return <PropertiesSkeleton />
  if (error && isNotFoundError(error)) {
    return (
      <DeviceNotFoundState
        onBack={onBackToDevices}
        actionLabel={backToDevicesLabel}
      />
    )
  }
  if (error || !device) return <LoadErrorState onRetry={() => mutate()} />

  const properties = configToProperties(device)

  const handleSave = async (next: Record<string, ConfigPropertyValue | null>) => {
    try {
      const patch = propertiesToUpdate(next)
      const updated = await update.trigger(patch, { revalidate: false })
      if (updated) await mutate(updated, { revalidate: false })
      toast({ variant: "success", title: "Device configuration updated" })
    } catch (err) {
      toast({
        variant: "error",
        title: "Failed to update device",
        description: err instanceof Error ? err.message : "Please try again.",
      })
      throw err
    }
  }

  return (
    <ConfigPropertiesPanel
      properties={properties}
      resetKey={deviceConfigResetKey(device)}
      onSave={handleSave}
      isSaving={update.isMutating}
      defaultEditing={defaultEditing}
      saveConfirmationTitle="Save device configuration changes?"
      saveConfirmationDescription="Are you sure you want to save these changes to this device?"
    />
  )
}

function PropertiesSkeleton() {
  return (
    <Card className="bg-card border-border">
      <CardContent className="p-6 space-y-4">
        {[...Array(8)].map((_, i) => (
          <div key={i} className="flex items-center gap-6">
            <Skeleton className="h-4 w-1/3 bg-muted" />
            <Skeleton className="h-9 flex-1 max-w-xs bg-muted" />
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function LoadErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <Card className="bg-card border-border">
      <CardContent className="flex flex-col items-center justify-center py-16">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10 mb-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="text-lg font-medium text-foreground mb-2">Failed to load device config</h3>
        <p className="text-sm text-muted-foreground mb-6 text-center max-w-sm">
          The device configuration could not be loaded. Please try again.
        </p>
        <Button onClick={onRetry} className="bg-primary hover:bg-primary/90 text-primary-foreground">
          Retry
        </Button>
      </CardContent>
    </Card>
  )
}
